import SVGArrowUp from 'visitor-ui-component-library-icons/icons/SVGArrowUp';
import VizExIcon from 'visitor-ui-component-library/icon/VizExIcon';
import { darken, lighten } from 'visitor-ui-component-library/utils/colors';
import { NEUTRAL_1600 } from 'visitor-ui-component-library/constants/WidgetColors';
import styled from 'styled-components';
import { useRef, useState } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SPOTLIGHT_WIDTH = 360;
const SPOTLIGHT_EXPANDED_WIDTH = 700;
const MAX_TEXTAREA_HEIGHT = 200;
const Container = styled.div.withConfig({
  displayName: "SpotlightLauncherShell__Container"
})(["position:relative;border:1px solid #ccc;border-radius:24px;padding:10px 8px;display:flex;flex-direction:column;gap:8px;min-height:48px;width:", "px;background-color:white;cursor:", ";box-sizing:border-box;margin-bottom:32px;transition:", ";"], ({
  $expanded
}) => $expanded ? SPOTLIGHT_EXPANDED_WIDTH : SPOTLIGHT_WIDTH, ({
  $disabled
}) => $disabled ? 'not-allowed' : 'text', ({
  $shouldAnimate
}) => $shouldAnimate ? 'width 500ms ease' : 'none');
const InputRow = styled.div.withConfig({
  displayName: "SpotlightLauncherShell__InputRow"
})(["display:flex;align-items:flex-start;gap:8px;width:100%;"]);
const InputArea = styled.div.withConfig({
  displayName: "SpotlightLauncherShell__InputArea"
})(["display:flex;align-items:flex-start;gap:8px;flex:1;min-width:0;padding-left:", ";"], ({
  $hasAddButton
}) => $hasAddButton ? '0' : '8px');
export const IconButton = styled.button.withConfig({
  displayName: "SpotlightLauncherShell__IconButton"
})(["width:28px;height:28px;border-radius:50%;background-color:", ";border:none;display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;padding:0;color:", ";transition:background-color 150ms ease,color 150ms ease;&:hover{background-color:", ";}&:disabled{pointer-events:none;}"], ({
  $active
}) => $active ? NEUTRAL_1600 : '#f0f0f0', ({
  $active
}) => $active ? 'white' : 'inherit', ({
  $active
}) => $active ? lighten(NEUTRAL_1600, 0.15) : darken('#f0f0f0', 0.15));
const SendIconButton = styled(IconButton).withConfig({
  displayName: "SpotlightLauncherShell__SendIconButton"
})(["align-self:flex-end;"]);
const textareaBaseStyles = `
  font-size: 14px;
  font-weight: 300;
  line-height: 24px;
  padding: 2px 0;
  word-break: break-word;
  white-space: pre-wrap;
  max-height: ${MAX_TEXTAREA_HEIGHT}px;
`;
const TextareaWrapper = styled.div.withConfig({
  displayName: "SpotlightLauncherShell__TextareaWrapper"
})(["display:grid;flex:1;min-width:0;&::after{content:attr(data-value) ' ';visibility:hidden;overflow:hidden;grid-area:1 / 1;", "}"], textareaBaseStyles);
const Textarea = styled.textarea.withConfig({
  displayName: "SpotlightLauncherShell__Textarea"
})(["background:transparent;border:none;outline:none;width:100%;color:", ";resize:none;overflow-y:hidden;max-height:", "px;grid-area:1 / 1;", " &:focus,&:focus-visible{box-shadow:none;outline:none;}&::placeholder{color:#666;}&:disabled{cursor:not-allowed;}"], NEUTRAL_1600, MAX_TEXTAREA_HEIGHT, textareaBaseStyles);
const SpotlightLauncherShell = ({
  placeholder,
  sendAriaLabel,
  onSubmit,
  onOpen,
  open = false,
  disabled = false,
  hasAttachments = false,
  expanded = false,
  shouldAnimate = true,
  addButton,
  addMenu,
  fileUploads,
  onExpandedChange
}) => {
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const [messageText, setMessageText] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const hasTextContent = Boolean(messageText.trim());
  const isExpansionActive = open || isExpanded || expanded;
  const handleChange = e => {
    setMessageText(e.target.value);
    if (wrapperRef.current) {
      wrapperRef.current.dataset.value = e.target.value;
    }
    e.target.style.overflowY = e.target.scrollHeight >= MAX_TEXTAREA_HEIGHT ? 'auto' : 'hidden';
  };
  const handleSubmit = () => {
    if (disabled) return;
    const hasContent = hasTextContent || hasAttachments;
    if (!hasContent) {
      onOpen();
      return;
    }
    onSubmit(messageText);
    setMessageText('');
    setIsExpanded(false);
    onExpandedChange === null || onExpandedChange === void 0 || onExpandedChange(false);
    if (wrapperRef.current) {
      wrapperRef.current.dataset.value = '';
    }
    if (inputRef.current) {
      inputRef.current.style.overflowY = 'hidden';
    }
  };
  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };
  const handleFocus = () => {
    setIsExpanded(true);
    onExpandedChange === null || onExpandedChange === void 0 || onExpandedChange(true);
  };
  const handleBlur = () => {
    const next = hasTextContent;
    setIsExpanded(next);
    onExpandedChange === null || onExpandedChange === void 0 || onExpandedChange(next);
  };
  return /*#__PURE__*/_jsxs(Container, {
    $expanded: isExpansionActive,
    $shouldAnimate: shouldAnimate,
    $disabled: disabled,
    "data-test-id": "spotlight-launcher",
    onClick: disabled ? undefined : () => {
      var _inputRef$current;
      return (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus();
    },
    children: [addMenu, fileUploads, /*#__PURE__*/_jsxs(InputRow, {
      children: [/*#__PURE__*/_jsxs(InputArea, {
        $hasAddButton: Boolean(addButton),
        children: [addButton, /*#__PURE__*/_jsx(TextareaWrapper, {
          ref: wrapperRef,
          children: /*#__PURE__*/_jsx(Textarea, {
            ref: inputRef,
            placeholder: placeholder,
            onKeyDown: handleKeyDown,
            value: messageText,
            onChange: handleChange,
            onFocus: handleFocus,
            onBlur: handleBlur,
            "aria-label": placeholder,
            rows: 1,
            disabled: disabled
          })
        })]
      }), /*#__PURE__*/_jsx(SendIconButton, {
        $active: hasTextContent || hasAttachments,
        "aria-pressed": hasTextContent || hasAttachments,
        onClick: handleSubmit,
        "aria-label": sendAriaLabel,
        "data-test-id": "arrow-up-icon",
        disabled: disabled,
        children: /*#__PURE__*/_jsx(VizExIcon, {
          size: "14px",
          icon: /*#__PURE__*/_jsx(SVGArrowUp, {})
        })
      })]
    })]
  });
};
SpotlightLauncherShell.displayName = 'SpotlightLauncherShell';
export default SpotlightLauncherShell;